/**
 * CRITICAL: Tracking URL Service - Ensures ALL QR codes use trackable URLs
 * 
 * This service generates tracking URLs that QR codes should embed instead of direct content URLs.
 * When users scan the QR code, they go through /qr-redirect/{shortCode} FIRST,
 * which records analytics, then redirects to the actual destination.
 * 
 * WITHOUT this service, QR codes would embed direct URLs and skip analytics entirely.
 */

import { supabase } from '../lib/supabase';

class TrackingUrlService {
  static getTrackingBaseUrl() {
    // Preferred: configure explicitly per environment (Netlify/Vite env var)
    const envBase = import.meta.env?.VITE_TRACKING_BASE_URL;
    const base = (envBase || window?.location?.origin || 'http://localhost:3000')?.trim();
    return base?.replace(/\/+$/, '');
  }
  
  /**
   * Generate trackable URL that should be embedded in QR codes
   * @param {string} shortCode - The short code from qr_codes.short_url
   * @returns {string} - Tracking URL like https://domain.com/qr/${shortCode}
   */
  static generateTrackingUrl(shortCode) {
    if (!shortCode) {
      console.warn('Cannot generate tracking URL without short code');
      return null;
    }

    const baseUrl = this.getTrackingBaseUrl();
    // CRITICAL: Generate the tracking URL that goes through the redirect system
    const trackingUrl = `${baseUrl}/qr/${shortCode}`;
    
    console.log(`TRACKING URL GENERATED: ${trackingUrl} (will track then redirect)`);
    
    return trackingUrl;
  }

  /**
   * Generate production tracking URL with custom domain
   * @param {string} shortCode - The short code from qr_codes.short_url  
   * @param {string} customDomain - Optional custom domain (defaults to current domain)
   * @returns {string} - Production tracking URL
   */
  static generateProductionTrackingUrl(shortCode, customDomain = null) {
    if (!shortCode) {
      console.warn('Cannot generate production tracking URL without short code');
      return null;
    }

    const domain = (customDomain || this.getTrackingBaseUrl())?.replace(/\/+$/, '');
    const trackingUrl = `${domain}/qr/${shortCode}`;
    
    console.log(`PRODUCTION TRACKING URL: ${trackingUrl}`);
    
    return trackingUrl;
  }

  /**
   * Check if a short code appears to be descriptive (contains words/hyphens) vs random
   * @param {string} shortCode - Short code to analyze
   * @returns {boolean} - True if appears descriptive, false if random
   */
  static isDescriptive(shortCode) {
    if (!shortCode || typeof shortCode !== 'string') {
      return false;
    }

    const code = shortCode?.toLowerCase()?.trim();
    
    // Consider it descriptive if it:
    // - Contains hyphens or underscores (word separators)
    // - Is longer than 6 characters and contains vowels (likely words)
    // - Contains common word patterns
    const hasWordSeparators = /[-_]/?.test(code);
    const hasVowels = /[aeiou]/?.test(code);
    const isLongerThanRandom = code?.length > 6;
    
    // Common descriptive patterns
    const hasDescriptivePatterns = /\b(wifi|menu|contact|info|promo|special|offer|sale|event|qr|code|link|url)\b/?.test(code);
    
    return hasWordSeparators || (isLongerThanRandom && hasVowels) || hasDescriptivePatterns;
  }

  /**
   * Generate descriptive short code based on a name or title
   * @param {string} name - Name/title to base the short code on
   * @returns {string} - Generated descriptive short code
   */
  static generateDescriptiveShortCode(name) {
    if (!name || typeof name !== 'string') {
      return 'qr-code';
    }

    // Clean the name: lowercase, remove special chars except spaces
    let cleaned = name?.toLowerCase()?.replace(/[^a-z0-9\s]/g, '')?.trim();
    
    // Replace multiple spaces with single space
    cleaned = cleaned?.replace(/\s+/g, ' ');
    
    // Split into words and take first 3 words max
    const words = cleaned?.split(' ')?.filter(word => word?.length > 0)?.slice(0, 3);
    
    if (words?.length === 0) {
      return 'qr-code';
    }
    
    // Join words with hyphens
    let shortCode = words?.join('-');
    
    // Ensure it's not too long (max 20 characters)
    if (shortCode?.length > 20) {
      // Try abbreviating words
      const abbreviated = words?.map(word => {
        if (word?.length > 4) {
          return word?.substring(0, 4);
        }
        return word;
      })?.join('-');
      
      shortCode = abbreviated?.length <= 20 ? abbreviated : abbreviated?.substring(0, 20);
    }
    
    // Ensure it's at least 3 characters
    if (shortCode?.length < 3) {
      shortCode = `${shortCode}-qr`;
    }
    
    return shortCode;
  }

  /**
   * Validate short code availability with suggestions
   * @param {string} shortCode - Short code to validate
   * @param {string} excludeId - QR ID to exclude from check (for updates)
   * @returns {Object} - Validation result with availability and suggestions
   */
  static async validateShortCodeAvailability(shortCode, excludeId = null) {
    try {
      if (!shortCode) {
        return {
          success: false,
          isAvailable: false,
          error: 'Short code cannot be empty',
          suggestions: []
        };
      }

      // Clean and validate format
      const cleanShortCode = shortCode?.toLowerCase()?.replace(/[^a-z0-9_-]/g, '');
      
      if (cleanShortCode !== shortCode?.toLowerCase()) {
        const suggestions = await this.generateAlternativeShortCodes(cleanShortCode);
        return {
          success: false,
          isAvailable: false,
          error: 'Short code can only contain letters, numbers, underscores, and hyphens',
          suggestions: [cleanShortCode, ...suggestions]
        };
      }

      if (cleanShortCode?.length < 2) {
        return {
          success: false,
          isAvailable: false,
          error: 'Short code must be at least 2 characters long',
          suggestions: []
        };
      }

      if (cleanShortCode?.length > 50) {
        const truncated = cleanShortCode?.substring(0, 50);
        const suggestions = await this.generateAlternativeShortCodes(truncated);
        return {
          success: false,
          isAvailable: false,
          error: 'Short code cannot exceed 50 characters',
          suggestions: [truncated, ...suggestions]
        };
      }

      // Build query to check if short code exists
      let query = supabase?.from('qr_codes')?.select('id, short_url')?.eq('short_url', cleanShortCode);
      
      // Exclude current QR if updating
      if (excludeId) {
        query = query?.neq('id', excludeId);
      }
      
      const { data: existingQR, error } = await query?.single();

      if (error && error?.code !== 'PGRST116') {
        // PGRST116 means no rows returned (good - available)
        throw error;
      }

      if (existingQR) {
        // Short code is taken, generate suggestions
        const suggestions = await this.generateAlternativeShortCodes(cleanShortCode);
        return {
          success: false,
          isAvailable: false,
          error: 'This short code is already taken',
          suggestions
        };
      }

      // Short code is available
      return {
        success: true,
        isAvailable: true,
        error: null,
        suggestions: []
      };

    } catch (error) {
      console.error('Error validating short code availability:', error);
      return {
        success: false,
        isAvailable: false,
        error: 'Unable to validate short code at this time',
        suggestions: []
      };
    }
  }

  /**
   * NEW: Validate if a custom short code is available
   * @param {string} shortCode - Custom short code to validate
   * @returns {Object} - Validation result with availability and suggestions
   */
  static async validateCustomShortCode(shortCode) {
    try {
      if (!shortCode) {
        return {
          isAvailable: false,
          error: 'Short code cannot be empty',
          suggestions: []
        };
      }

      // Clean and validate format
      const cleanShortCode = shortCode?.toLowerCase()?.replace(/[^a-z0-9_-]/g, '');
      
      if (cleanShortCode !== shortCode) {
        return {
          isAvailable: false,
          error: 'Short code can only contain letters, numbers, underscores, and hyphens',
          suggestions: [cleanShortCode]
        };
      }

      if (cleanShortCode?.length < 2) {
        return {
          isAvailable: false,
          error: 'Short code must be at least 2 characters long',
          suggestions: []
        };
      }

      if (cleanShortCode?.length > 50) {
        return {
          isAvailable: false,
          error: 'Short code cannot exceed 50 characters',
          suggestions: [cleanShortCode?.substring(0, 50)]
        };
      }

      // Check if short code already exists in database
      const { data: existingQR, error } = await supabase?.from('qr_codes')?.select('id, short_url')?.eq('short_url', cleanShortCode)?.single();

      if (error && error?.code !== 'PGRST116') {
        // PGRST116 means no rows returned (good - available)
        throw error;
      }

      if (existingQR) {
        // Short code is taken, generate suggestions
        const suggestions = await this.generateAlternativeShortCodes(cleanShortCode);
        return {
          isAvailable: false,
          error: 'This short code is already taken',
          suggestions
        };
      }

      // Short code is available
      return {
        isAvailable: true,
        error: null,
        suggestions: []
      };

    } catch (error) {
      console.error('Error validating custom short code:', error);
      return {
        isAvailable: false,
        error: 'Unable to validate short code at this time',
        suggestions: []
      };
    }
  }

  /**
   * NEW: Generate alternative short codes when one is taken
   * @param {string} baseShortCode - Base short code to generate alternatives for
   * @returns {Array} - Array of suggested alternatives
   */
  static async generateAlternativeShortCodes(baseShortCode) {
    const suggestions = [];
    const maxSuggestions = 6;

    try {
      // Generate numbered alternatives
      for (let i = 1; i <= 3; i++) {
        const suggestion = `${baseShortCode}${i}`;
        const { data } = await supabase?.from('qr_codes')?.select('id')?.eq('short_url', suggestion)?.single();
        
        if (!data) suggestions?.push(suggestion);
        if (suggestions?.length >= maxSuggestions) break;
      }

      // Generate alternatives with common suffixes
      const suffixes = ['_new', '_v2', '_alt', '-2024', '_qr'];
      for (const suffix of suffixes) {
        if (suggestions?.length >= maxSuggestions) break;
        
        const suggestion = `${baseShortCode}${suffix}`;
        const { data } = await supabase?.from('qr_codes')?.select('id')?.eq('short_url', suggestion)?.single();
        
        if (!data) suggestions?.push(suggestion);
      }

      // Generate random alternatives if we don't have enough
      while (suggestions?.length < maxSuggestions) {
        const randomSuffix = Math.random()?.toString(36)?.substring(2, 5);
        const suggestion = `${baseShortCode}_${randomSuffix}`;
        
        const { data } = await supabase?.from('qr_codes')?.select('id')?.eq('short_url', suggestion)?.single();
        
        if (!data) suggestions?.push(suggestion);
        
        // Prevent infinite loop
        if (suggestions?.length === 0 && Math.random() > 0.5) break;
      }

    } catch (error) {
      console.error('Error generating alternative short codes:', error);
    }

    return suggestions?.slice(0, maxSuggestions);
  }

  /**
   * NEW: Generate short code suggestions based on QR name
   * @param {string} name - QR code name
   * @param {string} content - QR code content (optional)
   * @returns {Object} - Result with suggestions
   */
  static async getShortCodeSuggestions(name, content = '') {
    try {
      if (!name || !name?.trim()) {
        return {
          success: false,
          error: 'QR name is required to generate suggestions',
          suggestions: []
        };
      }

      const suggestions = [];
      const cleanName = name?.toLowerCase()?.replace(/[^a-z0-9\s]/g, '')?.replace(/\s+/g, '-')?.substring(0, 20);

      // Generate variations of the name
      const baseOptions = [
        cleanName,
        cleanName?.replace(/-/g, '_'),
        cleanName?.replace(/-/g, ''),
        `qr_${cleanName}`,
        `${cleanName}_code`,
        `my_${cleanName}`
      ];

      // Check availability for each base option
      for (const base of baseOptions) {
        if (!base || base?.length < 2) continue;

        const { data } = await supabase?.from('qr_codes')?.select('id')?.eq('short_url', base)?.single();
        
        if (!data) {
          suggestions?.push(base);
        }

        if (suggestions?.length >= 6) break;
      }

      // If we don't have enough suggestions, add numbered variations
      if (suggestions?.length < 6 && cleanName?.length >= 2) {
        for (let i = 1; i <= 10; i++) {
          const numbered = `${cleanName}${i}`;
          
          const { data } = await supabase?.from('qr_codes')?.select('id')?.eq('short_url', numbered)?.single();
          
          if (!data) {
            suggestions?.push(numbered);
          }

          if (suggestions?.length >= 6) break;
        }
      }

      return {
        success: true,
        suggestions: suggestions?.slice(0, 6),
        error: null
      };

    } catch (error) {
      console.error('Error generating short code suggestions:', error);
      return {
        success: false,
        error: 'Unable to generate suggestions at this time',
        suggestions: []
      };
    }
  }

  /**
   * Validate if a URL is a tracking URL (contains /qr/ path)
   * @param {string} url - URL to validate
   * @returns {boolean} - True if this is a tracking URL
   */
  static isTrackingUrl(url) {
    if (!url) return false;
    
    return url?.includes('/qr/') && !url?.includes('/qr-redirect/');
  }

  /**
   * Extract short code from tracking URL
   * @param {string} trackingUrl - The tracking URL
   * @returns {string|null} - Extracted short code or null
   */
  static extractShortCodeFromUrl(trackingUrl) {
    if (!trackingUrl) return null;
    
    const match = trackingUrl?.match(/\/qr\/([^/?#]+)/);
    return match ? match?.[1] : null;
  }

  /**
   * Convert a direct content URL to a tracking URL
   * @param {string} contentUrl - Direct destination URL
   * @param {string} shortCode - Short code for this QR
   * @returns {string} - Tracking URL that should be used in QR code
   */
  static convertToTrackingUrl(contentUrl, shortCode) {
    if (!shortCode) {
      console.warn('Cannot create tracking URL without short code, falling back to direct URL');
      return contentUrl;
    }

    const trackingUrl = this.generateTrackingUrl(shortCode);
    
    console.log(`URL CONVERSION: ${contentUrl} -> ${trackingUrl} (for analytics)`);
    
    return trackingUrl;
  }

  /**
   * CRITICAL: Validate QR data to ensure it uses tracking URLs
   * @param {Object} qrData - QR code data
   * @returns {Object} - Validation result with warnings
   */
  static validateQRForTracking(qrData) {
    const result = {
      isValid: true,
      warnings: [],
      trackingEnabled: false
    };

    if (!qrData) {
      result.isValid = false;
      result?.warnings?.push('No QR data provided');
      return result;
    }

    // Check if QR code has short_url for tracking
    if (!qrData?.short_url) {
      result.isValid = false;
      result?.warnings?.push('QR code missing short_url - cannot generate tracking URL');
      return result;
    }

    // Check if content is direct URL vs tracking URL
    if (qrData?.content && !this.isTrackingUrl(qrData?.content)) {
      result?.warnings?.push('QR code content appears to be direct URL - should use tracking URL for analytics');
      result.trackingEnabled = false;
    } else {
      result.trackingEnabled = true;
    }

    return result;
  }

  /**
   * Get the redirect page URL path for a short code
   * @param {string} shortCode - Short code
   * @returns {string} - Redirect page path
   */
  static getRedirectPagePath(shortCode) {
    return `/qr-redirect/${shortCode}`;
  }

  /**
   * IMPORTANT: Check if current environment supports tracking
   * @returns {Object} - Environment info for tracking
   */
  static getTrackingEnvironment() {
    return {
      domain: window?.location?.origin || 'localhost:3000',
      supportsTracking: true,
      redirectPath: '/qr-redirect/',
      trackingPath: '/qr/'
    };
  }

  /**
   * Get tracking URL for existing QR code
   * @param {string} qrCodeId - QR code ID
   * @returns {Object} - Result with tracking URL
   */
  static async getTrackingUrlForQRCode(qrCodeId) {
    try {
      const { data: qrCode, error } = await supabase?.from('qr_codes')?.select('short_url')?.eq('id', qrCodeId)?.single();
      
      if (error) {
        throw error;
      }
      
      if (!qrCode?.short_url) {
        return {
          success: false,
          error: 'QR code has no short URL',
          data: null
        };
      }
      
      const trackingUrl = this.generateTrackingUrl(qrCode?.short_url);
      
      return {
        success: true,
        data: {
          trackingUrl,
          shortCode: qrCode?.short_url
        },
        error: null
      };
      
    } catch (error) {
      console.error('Error getting tracking URL for QR code:', error);
      return {
        success: false,
        error: 'Failed to get tracking URL',
        data: null
      };
    }
  }
}

export { TrackingUrlService };